import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Stack,
  Alert,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  AttachFile as AttachFileIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_URL } from '../config';
import { compressImageIfNeeded, getCompressionInfo, getCompressionOptions } from '../utils/imageCompression';

const OrderAttachments = ({ 
  orderId, 
  attachments = [], 
  onAttachmentsChange,
  mode = 'full', // 'full', 'upload-only', 'display-only'
  maxFiles = 10,
  allowedTypes = ['image/*', '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt', '.csv'],
  title = 'Order Attachments',
  type = 'order', // 'order' or 'offer'
  tempAttachments = [],
  onTempAttachmentsChange
}) => {
  const { getToken } = useAuth();
  const [localAttachments, setLocalAttachments] = useState(attachments || []);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [imageViewerDialog, setImageViewerDialog] = useState({ 
    open: false, 
    imageUrl: '', 
    imageName: '' 
  });
  
  // Ref to track if we should call onAttachmentsChange
  const onAttachmentsChangeRef = useRef(onAttachmentsChange);

  // Determine if we're in "prop mode" (order creation) or "fetch mode" (order viewing)
  // Only consider it "prop mode" if attachments is explicitly provided AND has meaningful data
  // Empty arrays passed unintentionally should still trigger fetch mode
  const isUsingPropsForData = attachments !== undefined && 
    (attachments.length > 0 || mode === 'upload-only');
  
  // Update local attachments when prop changes (only in prop mode)
  useEffect(() => {
    // Only update from props if we're explicitly in "prop mode" (attachments prop was passed)
    if (isUsingPropsForData && Array.isArray(attachments) && attachments.length !== localAttachments.length) {
      setLocalAttachments(attachments);
    }
  }, [attachments, isUsingPropsForData]); // Keep localAttachments out of dependencies to prevent loops

  // Utility functions
  const isImageFile = (filename, mimeType) => {
    // Check MIME type first (most reliable)
    if (mimeType && mimeType.startsWith('image/')) return true;
    // Fall back to extension check
    if (!filename) return false;
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    return imageExtensions.some(ext => filename.toLowerCase().endsWith(ext));
  };

  const getAttachmentUrl = (filePath) => {
    if (!filePath) return '';
    const baseUrl = API_URL.replace('/api', '');
    return `${baseUrl}/uploads/${filePath}`;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (mimeType) => {
    if (mimeType && mimeType.startsWith('image/')) return <ImageIcon />;
    return <AttachFileIcon />;
  };

  // Fetch attachments from server
  const fetchAttachments = useCallback(async () => {
    if (!orderId) return;

    try {
      const token = await getToken();
      const endpoint = type === 'offer' 
        ? `${API_URL}/offer-attachments/${orderId}`
        : `${API_URL}/order-attachments/${orderId}`;
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setLocalAttachments(response.data.data);
        
        if (onAttachmentsChangeRef.current && mode === 'full') {
          onAttachmentsChangeRef.current(response.data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching attachments:', error);
      setMessage({ 
        type: 'error', 
        text: 'Error loading attachments'
      });
    }
  }, [orderId, getToken, type]); // Removed onAttachmentsChange from dependencies

  // Load attachments when component mounts (only in fetch mode)
  useEffect(() => {
    if (!isUsingPropsForData && orderId && (mode === 'full' || mode === 'display-only')) {
      fetchAttachments();
    }
  }, [orderId, mode, fetchAttachments, isUsingPropsForData]);

  // Handle file selection and upload
  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    if (localAttachments.length + files.length > maxFiles) {
      setMessage({
        type: 'error',
        text: `Maximum ${maxFiles} files allowed`
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setMessage({ type: '', text: '' });

    try {
      const formData = new FormData();
      
      // Process each file (compress images if needed)
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        if (isImageFile(file.name, file.type)) {
          try {
            const compressionOptions = getCompressionOptions('general');
            const processedFile = await compressImageIfNeeded(file, compressionOptions);
            formData.append('attachments', processedFile);
            
            const compressionInfo = getCompressionInfo(file, processedFile);
            if (compressionInfo.wasCompressed) {
            }
          } catch (compressionError) {
            console.warn('Image compression failed, using original:', compressionError);
            formData.append('attachments', file);
          }
        } else {
          formData.append('attachments', file);
        }
      }

      const token = await getToken();
      const endpoint = type === 'offer' 
        ? `${API_URL}/offer-attachments/${orderId}`
        : `${API_URL}/order-attachments/${orderId}`;
      
      const response = await axios.post(
        endpoint,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          }
        }
      );

      if (response.data.success) {
        setMessage({
          type: 'success',
          text: `${files.length} file(s) uploaded successfully`
        });
        
        // Refresh attachments list
        await fetchAttachments();
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error uploading files'
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // Clear file input
      event.target.value = '';
    }
  };

  // Handle attachment deletion
  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) {
      return;
    }

    try {
      const token = await getToken();
      const endpoint = type === 'offer'
        ? `${API_URL}/offer-attachments/${attachmentId}`
        : `${API_URL}/order-attachments/${attachmentId}`;
      
      const response = await axios.delete(
        endpoint,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setMessage({
          type: 'success',
          text: 'Attachment deleted successfully'
        });
        
        // Refresh attachments list
        await fetchAttachments();
      }
    } catch (error) {
      console.error('Error deleting attachment:', error);
      setMessage({
        type: 'error',
        text: 'Error deleting attachment'
      });
    }
  };

  // Handle attachment download
  const handleDownloadAttachment = async (attachmentId, originalName) => {
    try {
      const token = await getToken();
      const endpoint = type === 'offer'
        ? `${API_URL}/offer-attachments/download/${attachmentId}`
        : `${API_URL}/order-attachments/download/${attachmentId}`;
      
      const response = await fetch(
        endpoint,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = originalName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
      } else {
        throw new Error('Download failed');
      }
    } catch (error) {
      console.error('Download error:', error);
      setMessage({
        type: 'error',
        text: 'Error downloading attachment'
      });
    }
  };

  // Handle image viewing
  const handleViewImage = (filePath, originalName) => {
    setImageViewerDialog({
      open: true,
      imageUrl: getAttachmentUrl(filePath),
      imageName: originalName
    });
  };

  // Clear messages after delay
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <Box>
      {/* Upload Section */}
      {(mode === 'full' || mode === 'upload-only') && orderId && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
              disabled={uploading || localAttachments.length >= maxFiles}
              fullWidth
            >
              {uploading ? 'Uploading...' : 'Upload Files'}
              <input
                type="file"
                hidden
                multiple
                accept={allowedTypes.join(',')}
                onChange={handleFileUpload}
                disabled={uploading}
              />
            </Button>
            
            {uploading && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                  {uploadProgress}% uploaded
                </Typography>
              </Box>
            )}
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Maximum {maxFiles} files. Supported types: Images, PDF, Word, Excel, Text files.
            <br />
            Maximum file size: 50MB per file. Images will be automatically compressed if too large.
          </Typography>
        </Paper>
      )}

      {/* Message Display */}
      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      {/* Attachments Display */}
      {(mode === 'full' || mode === 'display-only') && (
        <Box>
          {!localAttachments || localAttachments.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No attachments
            </Typography>
          ) : (
            <Stack spacing={2}>
              {localAttachments.map((attachment) => (
                <Card key={attachment.id} sx={{ display: 'flex', alignItems: 'center' }}>
                  {/* Image Preview or File Icon */}
                  <Box sx={{ width: 100, height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
                    {isImageFile(attachment.originalName, attachment.mimeType) ? (
                      <CardMedia
                        component="img"
                        sx={{
                          width: 100,
                          height: 100,
                          objectFit: 'cover',
                          cursor: 'pointer'
                        }}
                        image={getAttachmentUrl(attachment.filePath)}
                        alt={attachment.originalName}
                        onClick={() => handleViewImage(attachment.filePath, attachment.originalName)}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <Box 
                      sx={{ 
                        display: isImageFile(attachment.originalName, attachment.mimeType) ? 'none' : 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                        flexDirection: 'column'
                      }}
                    >
                      {getFileIcon(attachment.mimeType)}
                    </Box>
                  </Box>

                  {/* File Details */}
                  <CardContent sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" noWrap>
                      {attachment.originalName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(attachment.fileSize)} • {attachment.uploadedAt ? new Date(attachment.uploadedAt).toLocaleDateString() : ''}
                    </Typography>
                    {attachment.uploaderName && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Uploaded by: {attachment.uploaderName}
                      </Typography>
                    )}
                  </CardContent>

                  {/* Actions */}
                  <Box sx={{ p: 1 }}>
                    {isImageFile(attachment.originalName, attachment.mimeType) && (
                      <Tooltip title="View full size">
                        <IconButton 
                          size="small" 
                          onClick={() => handleViewImage(attachment.filePath, attachment.originalName)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    
                    <Tooltip title="Download">
                      <IconButton 
                        size="small" 
                        onClick={() => handleDownloadAttachment(attachment.id, attachment.originalName)}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>

                    {(mode === 'full') && (
                      <Tooltip title="Delete">
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleDeleteAttachment(attachment.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Card>
              ))}
            </Stack>
          )}
        </Box>
      )}

      {/* Image Viewer Dialog */}
      <Dialog 
        open={imageViewerDialog.open} 
        onClose={() => setImageViewerDialog({ open: false, imageUrl: '', imageName: '' })} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {imageViewerDialog.imageName}
          <IconButton 
            onClick={() => setImageViewerDialog({ open: false, imageUrl: '', imageName: '' })}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              minHeight: 400,
              bgcolor: 'grey.100'
            }}
          >
            <Box
              component="img"
              src={imageViewerDialog.imageUrl}
              alt={imageViewerDialog.imageName}
              sx={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <Box 
              sx={{ 
                display: 'none',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
                minHeight: 200
              }}
            >
              <ImageIcon sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="body1">Unable to load image</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => {
              const attachment = localAttachments.find(att =>
                getAttachmentUrl(att.filePath) === imageViewerDialog.imageUrl
              );
              if (attachment) {
                handleDownloadAttachment(attachment.id, attachment.originalName);
              }
            }}
          >
            Download
          </Button>
          <Button onClick={() => setImageViewerDialog({ open: false, imageUrl: '', imageName: '' })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderAttachments;